import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Key, Eye, EyeOff, Copy, Check, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { vaultAPI } from '../../services/api';

/**
 * Embedded Password Component
 * Displays an inline password reference with click-to-reveal functionality
 * Used for embedding passwords in articles, notes, and asset descriptions
 */
export default function EmbeddedPassword({ passwordId, name, showLink = true }) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealedValue, setRevealedValue] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Reveal password mutation
  const revealMutation = useMutation({
    mutationFn: () => vaultAPI.revealField(passwordId, 'password'),
    onSuccess: (response) => {
      setRevealedValue(response.data.value);
      setIsRevealed(true);
      setError(null);
      // Auto-hide after 30 seconds
      setTimeout(() => {
        setIsRevealed(false);
        setRevealedValue(null);
      }, 30000);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to reveal password');
    },
  });

  const handleReveal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isRevealed) {
      setIsRevealed(false);
      setRevealedValue(null);
    } else {
      revealMutation.mutate();
    }
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!revealedValue) return;
    await navigator.clipboard.writeText(revealedValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Compact inline display when not revealed
  if (!isRevealed) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-700 dark:text-amber-400 text-sm">
        <Key className="w-3.5 h-3.5" />
        <span className="font-medium">{name || 'Password'}</span>
        <button
          onClick={handleReveal}
          disabled={revealMutation.isPending}
          className="p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded transition-colors"
          title="Reveal password"
        >
          {revealMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Eye className="w-3.5 h-3.5" />
          )}
        </button>
        {showLink && (
          <Link
            to={`/vault/${passwordId}`}
            className="p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded transition-colors"
            title="View password details"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </span>
    );
  }

  // Expanded display when revealed
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm">
      <Key className="w-4 h-4 text-green-600 dark:text-green-400" />
      <span className="font-medium text-green-700 dark:text-green-300">{name || 'Password'}:</span>
      <code className="px-2 py-0.5 bg-white dark:bg-neutral-800 rounded font-mono text-neutral-900 dark:text-white">
        {revealedValue}
      </code>
      <button
        onClick={handleCopy}
        className="p-1 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 rounded transition-colors"
        title="Copy to clipboard"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
      <button
        onClick={handleReveal}
        className="p-1 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 rounded transition-colors"
        title="Hide password"
      >
        <EyeOff className="w-4 h-4" />
      </button>
      {error && (
        <span className="text-red-600 dark:text-red-400 text-xs">{error}</span>
      )}
    </span>
  );
}
